import React, { ReactNode } from "react";
import styles from "./PageLayout.module.css";
import { useNavigate } from "react-router-dom";
import { Button } from "../../controls/Button/Button";
import { IconButton } from "../../icons/IconButton/IconButton";
import { TrashIcon } from "lucide-react";

type PageAction = {
    label: ReactNode;
    onClick: () => void;
    loading?: boolean;
    disabled?: boolean;
    visible?: boolean; // optional access gate
};

interface Props {
    pageTitle: ReactNode;
    children: ReactNode;

    headerChildren?: ReactNode;

    /**
     * A bar pinned below the scrollable content, outside it — e.g. a mobile primary-action bar
     * that must stay reachable while the page scrolls underneath it. Renders nothing when omitted.
     */
    bottomBar?: ReactNode;

    scrollable?: boolean;
    scrollContainerId?: string;

    /**
     * Below 768 px, give the actions a full-width row of their own under the title and let them
     * share it evenly, instead of squeezing them in beside the heading.
     *
     * Opt-in: a page with one short action still reads better on the title's line. Reach for this
     * when there are two actions, or one whose label is a sentence.
     */
    stackActionsOnMobile?: boolean;

    back?: boolean;
    backLabel?: ReactNode;
    backTo?: string;

    primaryAction?: PageAction;
    secondaryAction?: PageAction;
    removeAction?: {
        onClick: () => void;
        visible?: boolean;
    };
}

const PageLayout: React.FC<Props> = ({
    pageTitle,
    children,
    headerChildren,
    bottomBar,

    scrollable = true,
    scrollContainerId = "scrollableDiv",
    stackActionsOnMobile = false,

    back = false,
    backLabel = "← Zurück",
    backTo,

    primaryAction,
    secondaryAction,
    removeAction,
}) => {
    const navigate = useNavigate();

    const handleBack = () => {
        if (backTo) navigate(backTo);
        else navigate(-1);
    };

    const showPrimary = primaryAction && (primaryAction.visible ?? true);
    const showSecondary = secondaryAction && (secondaryAction.visible ?? true);
    const showRemove = removeAction && (removeAction.visible ?? true);

    return (
        <div className={styles.page}>
            <div className={`${styles.top} ${stackActionsOnMobile ? styles.stackActions : ""}`}>
                {/* Mobile: back button above title */}
                {back && (
                    <Button
                        className={`${styles.topButton} ${styles.goBack} ${styles.backMobile} `}
                        onClick={handleBack}
                        type="button"
                    >
                        {backLabel}
                    </Button>
                )}

                <div className={styles.mainRow}>
                    <h1 className="font-['PT_Sans'] text-[48px] font-bold leading-[1.1] text-[#16499A] max-[1080px]:text-[36px]">{pageTitle}</h1>

                    <div className={styles.buttonRow}>
                        {headerChildren}

                        {/* Desktop: back inside button row */}
                        {back && (
                            <Button
                                className={`${styles.topButton} ${styles.goBack} ${styles.backDesktop}`}
                                onClick={handleBack}
                                type="button"
                            >
                                {backLabel}
                            </Button>
                        )}

                        {showRemove && (
                            <IconButton
                                onClick={removeAction.onClick}
                                icon={<TrashIcon />}
                                aria-label="Remove item"
                                className={styles.removeButton}
                            />
                        )}

                        {showRemove && showPrimary && (
                            <div className={styles.actionDivider} />
                        )}

                        {showSecondary && (
                            <Button
                                className={styles.topButton}
                                onClick={secondaryAction.onClick}
                                isLoading={secondaryAction.loading}
                                disabled={secondaryAction.disabled}
                                type="button"
                                variant='neutral'
                            >
                                {secondaryAction.label}
                            </Button>
                        )}

                        {showPrimary && (
                            <Button
                                className={styles.topButton}
                                onClick={primaryAction.onClick}
                                isLoading={primaryAction.loading}
                                disabled={primaryAction.disabled}
                                type="button"
                                variant='surface'
                            >
                                {primaryAction.label}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div
                id={scrollContainerId}
                className={`${styles.content} ${scrollable ? "overflow-y-auto" : "overflow-hidden"}`}
            >
                {children}
            </div>

            {bottomBar}
        </div>
    );
};

export default PageLayout;
